// 카리아카 store.js 포크. 콘솔이 쓰는 상태만 남겼다(클래스·학생·결제 페이지·하와이매쓰 권한 등은 뺐다).
// 카리페이는 가맹점(대표) 한 종류만 있어 기관장(가맹점) 모드가 항상 켜져 있다.
// 저장 키(admin·user_info·corp_info·refresh_token)는 카리아카와 같다 — 오리진이 달라 섞이지 않는다.
import { action, observable } from "mobx";

const EMPTY_BUSINESS_REGISTRATION_FORM = () => ({
  step: 1, // 현재 진행 중인 Step
  // 기본 정보
  businessOwnerType: null, // "CORPORATE" | "INDIVIDUAL"
  businessName: "", // 상호명
  branchName: "", // 지점명
  spotPhoneNumber: "", // 사업장 전화번호
  // 카드 단말기 관련
  terminalOwn: null, // 카드 단말기 보유 여부 (true | false)
  onlineCatId: null, // 카드 단말기 CAT ID
  // 이미지 파일들 (Image ID)
  businessLicenseImageId: null, // 사업자 등록증
  bankbookImageId: null, // 통장사본
  corpRepresentativeIdImageIds: [], // 대표자 신분증 (배열)
  corpSealImageId: null, // 법인인감증명서 (법인만)
  corpRegistrationCertificateImageId: null, // 법인등기부등본 (법인만)
  shareHolderListImageId: null, // 주주명부 (법인만)
  corpFoundationImageId: null, // 면세 사업 증빙 서류
  spotInfoImageIds: [], // 매장 정보 사진 (배열)
  privacyAgreement: null, // 개인정보 수집 동의 여부 (true | false)
  senderDelegationAgreement: null, // 발신번호 등록 위임 동의 여부 (선택, true | false)
});

const readJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (e) {
    return null;
  }
};

export const rootStore = observable({
  me: null,
  user_info: null,
  corp_info: null,
  _token: "", // 내부 토큰 저장용

  // 포인트 설정 모달 안의 확인용 서브 모달
  settingSubModal: null,

  // 헤더 매출 갱신 이벤트(청구서 발송·수납 기록 뒤)
  shouldRefreshSales: false,

  // 사업장 등록
  businessRegistrationRetryModal: false,
  businessRegistrationForm: EMPTY_BUSINESS_REGISTRATION_FORM(),

  // 포인트 모달
  isPointModalOpen: false,
  isPointRefundModalOpen: false,
  isPointSettingModalOpen: false,

  /* ----------------------------------
   * 헤더 매출 갱신 이벤트
   ---------------------------------- */
  triggerSalesRefresh: action(function () {
    rootStore.shouldRefreshSales = true;
  }),
  clearSalesRefresh: action(function () {
    rootStore.shouldRefreshSales = false;
  }),

  /* ----------------------------------
   * 로그인 / 로그아웃
   ---------------------------------- */
  signIn: action((user, platform) => {
    rootStore.me = { ...user, platform };
    localStorage.setItem("admin", JSON.stringify(rootStore.me));
  }),

  signOut: action(() => {
    rootStore.me = null;
    rootStore.user_info = null;
    rootStore.corp_info = null;
    rootStore._token = "";
    rootStore.businessRegistrationForm = EMPTY_BUSINESS_REGISTRATION_FORM();
    localStorage.removeItem("admin");
    localStorage.removeItem("user_info");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("corp_info");
    // 보존: saved_email(로그인 화면 "이메일 저장하기")
    sessionStorage.clear();
  }),

  /* ----------------------------------
   * 토큰
   ---------------------------------- */
  // 앱 시작 시 토큰 복원
  initializeToken: action(() => {
    if (!rootStore._token && rootStore.getMe?.accessToken) {
      rootStore._token = rootStore.getMe.accessToken;
    }
  }),

  setToken: action((accessToken) => {
    rootStore._token = accessToken;
    const me = rootStore.getMe ?? {};
    rootStore.me = { ...me, accessToken };
    localStorage.setItem("admin", JSON.stringify(rootStore.me));
  }),

  setRefreshToken: action((refreshToken) => {
    if (refreshToken) localStorage.setItem("refresh_token", refreshToken);
  }),

  /* ----------------------------------
   * 프로필 · 사업장
   ---------------------------------- */
  setProfile: action((user_info) => {
    rootStore.user_info = user_info;
    localStorage.setItem("user_info", JSON.stringify(user_info));
  }),

  setCorpInfo: action(function (corpInfo) {
    rootStore.corp_info = corpInfo;
    localStorage.setItem("corp_info", JSON.stringify(corpInfo));
  }),

  setMeFromStorage: action(function (adminData) {
    rootStore.me = adminData;
  }),
  setUserInfoFromStorage: action(function (userInfo) {
    rootStore.user_info = userInfo;
  }),
  setCorpInfoFromStorage: action(function (corpInfo) {
    rootStore.corp_info = corpInfo;
  }),

  /* ----------------------------------
   * 포인트 모달
   ---------------------------------- */
  setPointModalOpen: action(function (val) {
    rootStore.isPointModalOpen = val;
  }),
  setPointRefundModalOpen: action(function (val) {
    rootStore.isPointRefundModalOpen = val;
  }),
  setPointSettingModalOpen: action(function (val) {
    rootStore.isPointSettingModalOpen = val;
  }),

  /* ----------------------------------
   * 사업장 등록 폼
   ---------------------------------- */
  setBusinessRegistrationRetryModal: action(function (value) {
    rootStore.businessRegistrationRetryModal = value;
  }),
  setBusinessRegistrationStep: action(function (step) {
    rootStore.businessRegistrationForm.step = step;
  }),
  setBusinessRegistrationField: action(function (key, value) {
    rootStore.businessRegistrationForm[key] = value;
  }),
  setBusinessRegistrationFields: action(function (fields) {
    Object.keys(fields).forEach((key) => {
      rootStore.businessRegistrationForm[key] = fields[key];
    });
  }),
  clearBusinessRegistrationForm: action(function () {
    rootStore.businessRegistrationForm = EMPTY_BUSINESS_REGISTRATION_FORM();
  }),

  /* ----------------------------------
   * 읽기 (새로고침 시 localStorage 에서 복원)
   ---------------------------------- */
  get getMe() {
    if (rootStore.me == null) {
      const adminData = readJson("admin");
      if (adminData) rootStore.setMeFromStorage(adminData);
    }
    return rootStore.me;
  },

  get getProfile() {
    if (rootStore.user_info == null) {
      const userInfo = readJson("user_info");
      if (userInfo) rootStore.setUserInfoFromStorage(userInfo);
    }
    return rootStore.user_info;
  },

  get getCorpInfo() {
    if (rootStore.corp_info == null) {
      const corpInfo = readJson("corp_info");
      if (corpInfo) rootStore.setCorpInfoFromStorage(corpInfo);
    }
    return rootStore.corp_info;
  },

  get isSignedIn() {
    return rootStore.getMe !== null;
  },

  get token() {
    if (!rootStore._token) {
      const accessToken = rootStore.getMe?.accessToken;
      if (accessToken) rootStore._token = accessToken;
    }
    return rootStore._token;
  },

  // 카리페이 콘솔은 가맹점 대표 전용이라 항상 기관장(가맹점) 모드다.
  get getCorpMode() {
    return true;
  },
});

export default rootStore;
